// Client-only: uses useState for toggle and modal state.
// In Next.js App Router, import this inside a "use client" boundary.
import { useState, type ReactNode } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'
import { PageHeader } from './atoms'
import { Switch } from './Switch'

// Languages we ship. Native endonyms so each reads in its own language. Draft
// (machine) translations for fi/no/de/fr/da are pending native review
// (CLUB-278/281/282 and the fr/da review tickets).
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'sv', label: 'Svenska' },
  { value: 'fi', label: 'Suomi' },
  { value: 'no', label: 'Norsk' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'da', label: 'Dansk' },
] as const
const SUPPORTED_LANGS = ['en', 'sv', 'fi', 'no', 'de', 'fr', 'da'] as const

// The shortest new password the change-password form accepts. Named so the
// validation and the interpolated error message stay in sync.
const MIN_PASSWORD_LENGTH = 8

/**
 * Why a password change can fail, in terms this component can render. The
 * container translates its auth provider's error codes into one of these, so
 * the modal owns the wording (its other strings live here too) without this
 * package depending on Firebase.
 *
 * Each reason earns its own message: collapsing them into one generic failure
 * leaves someone retyping a perfectly good new password because we never told
 * them it was the OLD one we rejected.
 */
export type ChangePasswordFailure =
  | 'wrong-password'
  | 'weak-password'
  | 'requires-recent-login'
  | 'too-many-requests'
  | 'unknown'

export type ChangePasswordOutcome = { ok: true } | { ok: false; reason: ChangePasswordFailure }

// Message key per failure reason, so a new reason can't be added without a
// string to render it — the record is exhaustive over the union.
const FAILURE_KEYS: Readonly<Record<ChangePasswordFailure, string>> = {
  'wrong-password': 'security.password.modal.errors.wrongPassword',
  'weak-password': 'security.password.modal.errors.weakPassword',
  'requires-recent-login': 'security.password.modal.errors.requiresRecentLogin',
  'too-many-requests': 'security.password.modal.errors.tooManyRequests',
  unknown: 'security.password.modal.errors.generic',
}

export interface SettingsPageProps {
  // Invoked when the user confirms account deletion (e.g. so a host can sign
  // the user out / navigate away). Its presence is also what makes the Danger
  // Zone appear at all: without it, confirming would show an "account deleted"
  // acknowledgement for an account that still exists.
  onDeleteAccount?: () => void
  /**
   * Render the sections whose controls are still design mockups with nothing
   * behind them — notification preferences, two-factor, and the region picker.
   * They keep local `useState` only: nothing is loaded, nothing is saved, and
   * reloading the page silently discards every change.
   *
   * The security section has left this list: the password card is wired to
   * `onChangePassword` and follows that handler instead, so it appears for a
   * host that can really change a password. Two-factor beside it is still a
   * mockup and stays gated here.
   *
   * Defaults to **false** so a host gets only controls that work, and any new
   * host has to opt in deliberately rather than inherit a fake settings page.
   * `apps/wehunt-web-prototype` opts in, because showing unbuilt designs is
   * that app's whole purpose.
   *
   * Delete a gate from this list as its section gains a real backend — the
   * point is for the flag to shrink to nothing, not to become permanent.
   */
  showUnimplemented?: boolean
  /** Persist a new password. Resolves with the outcome — success is claimed
   *  only when this says so, never before it has been called. Its presence is
   *  what makes the Security section appear: the previous modal validated
   *  client-side and then said "Your password has been changed" without
   *  calling anything, and a prop the container has to supply is what stops
   *  that from being possible. */
  onChangePassword?: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<ChangePasswordOutcome>
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="wh-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div
        style={{
          padding: '16px 22px',
          borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          fontSize: 15,
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  )
}

function SettingsRow({
  title,
  subtitle,
  control,
  last,
}: {
  title: string
  subtitle?: string
  control: ReactNode
  last?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 22px',
        borderBottom: last ? 'none' : `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: COLORS.outline, lineHeight: 1.5 }}>{subtitle}</div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>{control}</div>
    </div>
  )
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string; disabled?: boolean }[]
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '8px 32px 8px 12px',
        border: `1.5px solid ${COLORS.surfaceContainerHighest}`,
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        background: COLORS.surfaceContainerLowest,
        outline: 'none',
        minWidth: 140,
        appearance: 'none',
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2354574f' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

function PasswordField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useTranslation('settings')
  // Reveal toggle so users (and testers) can confirm exactly what they typed —
  // e.g. catch a browser-autofilled old password in the "new" field.
  const [reveal, setReveal] = useState(false)
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        {label}
      </span>
      <div style={{ position: 'relative' }}>
        <input
          type={reveal ? 'text' : 'password'}
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 46px 10px 12px',
            border: `1.5px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 6,
            fontSize: 14,
            background: COLORS.surfaceContainerLowest,
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          aria-label={reveal ? t('security.password.modal.hide') : t('security.password.modal.show')}
          aria-pressed={reveal}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            width: 34, height: 34, border: 'none', background: 'transparent',
            color: COLORS.outline, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name={reveal ? 'eye-off' : 'eye'} size={20} />
        </button>
      </div>
    </label>
  )
}

function ModalShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surfaceContainerLowest,
          borderRadius: 12,
          maxWidth: 460,
          width: '100%',
          padding: 28,
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function SettingsPage({
  onDeleteAccount,
  showUnimplemented = false,
  onChangePassword,
}: SettingsPageProps = {}) {
  const [prefs, setPrefs] = useState({
    orderUpdates: true,
    subscriptionReminders: true,
    warrantyAlerts: true,
    promotional: false,
    newsletter: true,
    twoFactor: false,
  })
  const { t, i18n } = useTranslation('settings')
  // Active UI language, normalized to a base code (e.g. 'sv-SE' -> 'sv').
  const rawLang = (i18n.language ?? 'sv').split('-')[0]
  // Bokmål/Nynorsk render the Norwegian bundle; the selector offers one
  // Norwegian entry, so they show as it.
  const currentLang = rawLang === 'nb' || rawLang === 'nn' ? 'no' : rawLang
  const [region, setRegion] = useState('global')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleted, setDeleted] = useState(false)

  // Change-password modal. The length and match checks are local; everything
  // that decides whether the password actually changed comes back from
  // onChangePassword.
  const [pwOpen, setPwOpen] = useState(false)
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwSuccess, setPwSuccess] = useState(false)

  const toggle = (key: keyof typeof prefs) => setPrefs((p) => ({ ...p, [key]: !p[key] }))

  const openPasswordModal = () => {
    setPwCurrent('')
    setPwNew('')
    setPwConfirm('')
    setPwError(null)
    setPwSaving(false)
    setPwSuccess(false)
    setPwOpen(true)
  }

  const submitPassword = async () => {
    if (!onChangePassword || pwSaving) return
    if (pwNew.length < MIN_PASSWORD_LENGTH) {
      setPwError(t('security.password.modal.tooShort', { min: MIN_PASSWORD_LENGTH }))
      return
    }
    if (pwNew !== pwConfirm) {
      setPwError(t('security.password.modal.mismatch'))
      return
    }
    setPwError(null)
    setPwSaving(true)
    try {
      const outcome = await onChangePassword(pwCurrent, pwNew)
      if (outcome.ok) {
        // Cleared on the way out: nothing downstream needs them, and the
        // success view replaces the form anyway.
        setPwCurrent('')
        setPwNew('')
        setPwConfirm('')
        setPwSuccess(true)
      } else {
        setPwError(t(FAILURE_KEYS[outcome.reason]))
      }
    } catch {
      // A handler that rejects instead of resolving a reason is a container
      // bug, but the user still gets a definite answer rather than a form
      // stuck mid-submit.
      setPwError(t(FAILURE_KEYS.unknown))
    } finally {
      setPwSaving(false)
    }
  }

  const confirmDeleteAccount = () => {
    setConfirmDelete(false)
    onDeleteAccount?.()
    setDeleted(true)
  }

  // Terminal acknowledgement after a confirmed deletion — a self-contained
  // outcome for hosts (the prototype) that don't navigate away on the callback.
  if (deleted) {
    return (
      <div
        className="wh-card"
        style={{ padding: 40, textAlign: 'center', maxWidth: 460, margin: '40px auto' }}
      >
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 22,
            fontWeight: 500,
            marginBottom: 10,
          }}
        >
          {t('danger.deleted.title')}
        </div>
        <div style={{ fontSize: 14, color: COLORS.outline, lineHeight: 1.5 }}>
          {t('danger.deleted.body')}
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      {showUnimplemented && (
      <SettingsSection title={t('notifications.title')}>
        <SettingsRow
          title={t('notifications.orderUpdates.title')}
          subtitle={t('notifications.orderUpdates.subtitle')}
          control={<Switch on={prefs.orderUpdates} onChange={() => toggle('orderUpdates')} />}
        />
        <SettingsRow
          title={t('notifications.subscriptionReminders.title')}
          subtitle={t('notifications.subscriptionReminders.subtitle')}
          control={
            <Switch on={prefs.subscriptionReminders} onChange={() => toggle('subscriptionReminders')} />
          }
        />
        <SettingsRow
          title={t('notifications.warrantyAlerts.title')}
          subtitle={t('notifications.warrantyAlerts.subtitle')}
          control={<Switch on={prefs.warrantyAlerts} onChange={() => toggle('warrantyAlerts')} />}
        />
        <SettingsRow
          title={t('notifications.promotional.title')}
          subtitle={t('notifications.promotional.subtitle')}
          control={<Switch on={prefs.promotional} onChange={() => toggle('promotional')} />}
        />
        <SettingsRow
          title={t('notifications.newsletter.title')}
          subtitle={t('notifications.newsletter.subtitle')}
          control={<Switch on={prefs.newsletter} onChange={() => toggle('newsletter')} />}
          last
        />
      </SettingsSection>
      )}

      {/* Gated on the callback, not on showUnimplemented: the password card is
          wired now, so this section belongs to any host that can change a
          password. Two-factor below it is still a mockup and stays gated. */}
      {(onChangePassword || showUnimplemented) && (
      <SettingsSection title={t('security.title')}>
        <SettingsRow
          title={t('security.password.title')}
          subtitle={t('security.password.subtitle')}
          control={
            <button
              className="wh-btn wh-btn-outline"
              disabled={!onChangePassword}
              onClick={openPasswordModal}
            >
              {t('security.password.change')}
            </button>
          }
          // Carries the section's bottom border when two-factor is not
          // rendered beneath it.
          last={!showUnimplemented}
        />
        {showUnimplemented && (
        <SettingsRow
          title={t('security.twoFactor.title')}
          subtitle={t('security.twoFactor.subtitle')}
          control={<Switch on={prefs.twoFactor} onChange={() => toggle('twoFactor')} />}
          last
        />
        )}
      </SettingsSection>
      )}

      <SettingsSection title={t('languageRegion.title')}>
        <SettingsRow
          title={t('languageRegion.language')}
          subtitle={LANGUAGE_OPTIONS.find((o) => o.value === currentLang)?.label ?? 'English'}
          control={
            <Select
              value={currentLang}
              onChange={(v) => {
                // Only switch to a language we actually ship; disabled options
                // can't be picked, but guard anyway. LanguageDetector persists
                // the choice to localStorage['lang'].
                if ((SUPPORTED_LANGS as readonly string[]).includes(v)) {
                  void i18n.changeLanguage(v)
                }
              }}
              options={[...LANGUAGE_OPTIONS]}
            />
          }
          // The only genuinely working control on this page, so it carries the
          // section's bottom border when the region picker below is hidden.
          last={!showUnimplemented}
        />
        {showUnimplemented && (
        <SettingsRow
          title={t('languageRegion.region')}
          subtitle={t(`languageRegion.regionOptions.${region}`)}
          control={
            <Select
              value={region}
              onChange={setRegion}
              options={[
                { value: 'global', label: t('languageRegion.regionOptions.global') },
                { value: 'sweden', label: t('languageRegion.regionOptions.sweden') },
                { value: 'finland', label: t('languageRegion.regionOptions.finland') },
                { value: 'norway', label: t('languageRegion.regionOptions.norway') },
                { value: 'germany', label: t('languageRegion.regionOptions.germany') },
              ]}
            />
          }
          last
        />
        )}
      </SettingsSection>

      {/* Gated on the callback, not just on showUnimplemented: with no host
          handler, confirming deletion shows "Your account and all associated
          data have been deleted" over an account that is still there. A button
          that lies about an irreversible action is worse than a missing one. */}
      {(onDeleteAccount || showUnimplemented) && (
      <div
        className="wh-card"
        style={{
          padding: 0,
          overflow: 'hidden',
          borderColor: COLORS.error,
          background: COLORS.errorContainer,
        }}
      >
        <div
          style={{
            padding: '16px 22px',
            borderBottom: `1px solid ${COLORS.error}`,
            fontSize: 15,
            fontWeight: 700,
            color: COLORS.error,
          }}
        >
          {t('danger.title')}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '20px 22px',
          }}
        >
          <div style={{ flex: 1, fontSize: 13, color: COLORS.outline }}>
            {t('danger.description')}
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            className="wh-btn"
            style={{ background: COLORS.error, color: COLORS.onError, border: 'none' }}
          >
            <Icon name="trash" size={16} />
            {t('danger.deleteAccount')}
          </button>
        </div>
      </div>
      )}

      {confirmDelete && (
        <ModalShell onClose={() => setConfirmDelete(false)}>
          <div
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 22,
              fontWeight: 500,
              marginBottom: 10,
            }}
          >
            {t('danger.confirm.title')}
          </div>
          <div
            style={{
              fontSize: 14,
              color: COLORS.outline,
              lineHeight: 1.5,
              marginBottom: 22,
            }}
          >
            {t('danger.confirm.body')}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="wh-btn wh-btn-outline" onClick={() => setConfirmDelete(false)}>
              {t('danger.confirm.cancel')}
            </button>
            <button
              className="wh-btn"
              onClick={confirmDeleteAccount}
              style={{ background: COLORS.error, color: COLORS.onError, border: 'none' }}
            >
              <Icon name="trash" size={16} />
              {t('danger.confirm.confirmButton')}
            </button>
          </div>
        </ModalShell>
      )}

      {pwOpen && (
        // Dismissing on the backdrop is ignored mid-write, matching the
        // disabled Cancel: closing then would throw away the outcome of a
        // change that is going through regardless.
        <ModalShell onClose={() => { if (!pwSaving) setPwOpen(false) }}>
          <div
            style={{
              fontFamily: COLORS.fontDisplay,
              fontSize: 22,
              fontWeight: 500,
              marginBottom: 18,
            }}
          >
            {t('security.password.modal.title')}
          </div>

          {pwSuccess ? (
            <>
              <div style={{ fontSize: 14, color: COLORS.outline, lineHeight: 1.5, marginBottom: 22 }}>
                {t('security.password.modal.success')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="wh-btn wh-btn-primary" onClick={() => setPwOpen(false)}>
                  {t('security.password.modal.done')}
                </button>
              </div>
            </>
          ) : (
            <>
              <PasswordField
                label={t('security.password.modal.current')}
                value={pwCurrent}
                onChange={setPwCurrent}
              />
              <PasswordField
                label={t('security.password.modal.new')}
                value={pwNew}
                onChange={setPwNew}
              />
              <PasswordField
                label={t('security.password.modal.confirm')}
                value={pwConfirm}
                onChange={setPwConfirm}
              />
              {pwError && (
                <div style={{ fontSize: 13, color: COLORS.error, marginBottom: 16 }} role="alert">
                  {pwError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="wh-btn wh-btn-outline"
                  disabled={pwSaving}
                  onClick={() => setPwOpen(false)}
                >
                  {t('security.password.modal.cancel')}
                </button>
                <button
                  className="wh-btn wh-btn-primary"
                  disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                  onClick={submitPassword}
                >
                  {pwSaving
                    ? t('security.password.modal.saving')
                    : t('security.password.modal.submit')}
                </button>
              </div>
            </>
          )}
        </ModalShell>
      )}
    </div>
  )
}
